/**
 * The editors that write `PUT /me/profile`: birth date, sex, height,
 * motivation and the lifestyle facts. Each sends the three required fields
 * from what is stored plus its own edit (`profileInputFrom`).
 */
import { profileOptions } from "@repo/client/data/profile";
import {
  type PreviousModality,
  type ProfileInput,
  type ProfileOutput,
  profileInput,
} from "@repo/contract/training";
import {
  "profile-editors.birth_error" as birthError,
  "profile-editors.birth_title" as birthTitle,
  "profile-editors.error_field" as errorField,
  "profile-editors.eyebrow_lifestyle" as eyebrowLifestyle,
  "profile-editors.eyebrow_you" as eyebrowYou,
  "profile-editors.height_label" as heightLabel,
  "onboarding.height.question" as heightQuestion,
  "intake.why.height.long" as heightWhy,
  "improve.modality.question" as modalityQuestion,
  "improve.modality.why" as modalityWhy,
  "profile-editors.motivation_error" as motivationError,
  "profile-editors.motivation_title" as motivationTitle,
  "improve.occupation.question" as occupationQuestion,
  "improve.occupation.why" as occupationWhy,
  "improve.protein.question" as proteinQuestion,
  "improve.protein.why" as proteinWhy,
  "profile-editors.sex_helper" as sexHelper,
  "onboarding.sex.question" as sexQuestion,
  "improve.sleep.question" as sleepQuestion,
  "profile-editors.supplement_title" as supplementTitle,
} from "@repo/i18n";
import { useApi } from "../../../data/api";
import { BigNumberForm } from "./big-number-form";
import { BirthForm } from "./birth-form";
import { ChoiceForm } from "./choice-form";
import {
  type EditorHost,
  EditorShell,
  useSaveFlow,
  useSectionRead,
} from "./editor-shell";
import { ModalitiesForm } from "./modalities-form";
import { MotivationForm } from "./motivation-form";
import {
  type LabelledOption,
  occupationOptions,
  proteinMealsOptions,
  sexOptions,
  supplementOptions,
} from "./options";
import {
  calendarDayOf,
  type DayParts,
  dayParts,
  profileInputFrom,
} from "./payloads";
import {
  SLEEP_DEFAULT_HOURS,
  SLEEP_DEFAULT_QUALITY,
  type SleepDraft,
  SleepForm,
} from "./sleep-form";
import { useEditorForm } from "./use-editor-form";
import { useProfileMutation } from "./use-editor-mutation";
import { type FieldErrors, hasErrorAt } from "./validate";

interface EditorProps {
  readonly host: EditorHost;
}

/** Errors on fields this editor does not show, as one generic line. */
export function strayError(
  errors: FieldErrors,
  shown: readonly string[]
): string | null {
  const stray = Object.keys(errors).some(
    (key) => !shown.some((field) => hasErrorAt({ [key]: "" }, field))
  );
  return stray ? errorField() : null;
}

/** The profile section, its mutation and the save flow, for one editor. */
function useProfileEditor<D>(
  host: EditorHost,
  toDraft: (profile: ProfileOutput | null) => D,
  toPatch: (draft: D) => Partial<ProfileInput>
) {
  const api = useApi();
  const read = useSectionRead(profileOptions(api));
  const flow = useSaveFlow(useProfileMutation(), host);
  const form = useEditorForm({
    onSubmit: (payload: ProfileInput) => flow.save(payload),
    schema: profileInput,
    server: read.data,
    toDraft,
    toPayload: (draft: D) => profileInputFrom(read.data, toPatch(draft)),
  });
  return { flow, form, read };
}

function todayParts(): DayParts {
  const now = new Date();
  return { d: now.getDate(), m: now.getMonth() + 1, y: now.getFullYear() };
}

export function BirthEditor({ host }: EditorProps) {
  const today = todayParts();
  const { read, form, flow } = useProfileEditor<DayParts>(
    host,
    (profile) =>
      (profile ? dayParts(profile.dateOfBirth) : null) ?? {
        d: 1,
        m: 1,
        y: today.y - 30,
      },
    (draft) => ({ dateOfBirth: calendarDayOf(draft) })
  );
  return (
    <EditorShell
      eyebrow={eyebrowYou()}
      failure={flow.failure ?? strayError(form.errors, ["dateOfBirth"])}
      host={host}
      loadFailed={read.loadFailed}
      loading={read.loading}
      onSave={form.submit}
      pending={flow.pending}
      title={birthTitle()}
    >
      <BirthForm
        error={form.errors.dateOfBirth === undefined ? undefined : birthError()}
        onChange={form.setDraft}
        today={today}
        value={form.draft}
      />
    </EditorShell>
  );
}

const HEIGHT_DEFAULT_CM = 170;

export function HeightEditor({ host }: EditorProps) {
  const { read, form, flow } = useProfileEditor<number>(
    host,
    (profile) => profile?.heightCm ?? HEIGHT_DEFAULT_CM,
    (heightCm) => ({ heightCm })
  );
  return (
    <EditorShell
      eyebrow={eyebrowYou()}
      failure={flow.failure ?? strayError(form.errors, ["heightCm"])}
      host={host}
      loadFailed={read.loadFailed}
      loading={read.loading}
      onSave={form.submit}
      pending={flow.pending}
      title={heightQuestion()}
    >
      <BigNumberForm
        label={heightLabel()}
        max={250}
        min={100}
        onChange={form.setDraft}
        previous={read.data?.heightCm ?? null}
        step={1}
        tip={heightWhy()}
        unit="cm"
        value={form.draft}
      />
    </EditorShell>
  );
}

export function MotivationEditor({ host }: EditorProps) {
  const { read, form, flow } = useProfileEditor<string>(
    host,
    (profile) => profile?.motivation ?? "",
    (text) => ({ motivation: text.trim() === "" ? null : text.trim() })
  );
  return (
    <EditorShell
      eyebrow={eyebrowYou()}
      failure={flow.failure ?? strayError(form.errors, ["motivation"])}
      host={host}
      loadFailed={read.loadFailed}
      loading={read.loading}
      onSave={form.submit}
      pending={flow.pending}
      title={motivationTitle()}
    >
      <MotivationForm
        error={
          form.errors.motivation === undefined ? undefined : motivationError()
        }
        onChange={form.setDraft}
        value={form.draft}
      />
    </EditorShell>
  );
}

export function SleepEditor({ host }: EditorProps) {
  const { read, form, flow } = useProfileEditor<SleepDraft>(
    host,
    (profile) => ({
      quality: profile?.sleepQuality ?? SLEEP_DEFAULT_QUALITY,
      weekday: profile?.sleepHoursWeekday ?? SLEEP_DEFAULT_HOURS,
      weekend: profile?.sleepHoursWeekend ?? SLEEP_DEFAULT_HOURS,
    }),
    (draft) => ({
      sleepHoursWeekday: draft.weekday,
      sleepHoursWeekend: draft.weekend,
      sleepQuality: draft.quality,
    })
  );
  const shown = ["sleepHoursWeekday", "sleepHoursWeekend", "sleepQuality"];
  const own = shown.some((field) => hasErrorAt(form.errors, field));
  return (
    <EditorShell
      eyebrow={eyebrowLifestyle()}
      failure={flow.failure ?? strayError(form.errors, shown)}
      host={host}
      loadFailed={read.loadFailed}
      loading={read.loading}
      onSave={form.submit}
      pending={flow.pending}
      title={sleepQuestion()}
    >
      <SleepForm
        error={own ? errorField() : undefined}
        onChange={form.setDraft}
        value={form.draft}
      />
    </EditorShell>
  );
}

export function ModalitiesEditor({ host }: EditorProps) {
  const { read, form, flow } = useProfileEditor<PreviousModality[]>(
    host,
    (profile) => [...(profile?.previousModalities ?? [])],
    (previousModalities) => ({ previousModalities })
  );
  return (
    <EditorShell
      disabled={form.draft.length === 0}
      eyebrow={eyebrowLifestyle()}
      failure={flow.failure ?? strayError(form.errors, [])}
      host={host}
      loadFailed={read.loadFailed}
      loading={read.loading}
      onSave={form.submit}
      pending={flow.pending}
      title={modalityQuestion()}
    >
      <p className="mb-4 text-base text-ink-2 leading-relaxed">
        {modalityWhy()}
      </p>
      <ModalitiesForm onChange={form.setDraft} value={form.draft} />
    </EditorShell>
  );
}

/** The single-choice profile facts. */
export type ProfileChoiceField =
  | "occupation"
  | "proteinMeals"
  | "proteinSupplement"
  | "sex";

interface ChoiceSpec {
  readonly eyebrow: () => string;
  readonly helper?: () => string;
  readonly options: () => readonly LabelledOption<string>[];
  readonly title: () => string;
}

const CHOICES: Readonly<Record<ProfileChoiceField, ChoiceSpec>> = {
  occupation: {
    eyebrow: eyebrowLifestyle,
    helper: occupationWhy,
    options: occupationOptions,
    title: occupationQuestion,
  },
  proteinMeals: {
    eyebrow: eyebrowLifestyle,
    helper: proteinWhy,
    options: proteinMealsOptions,
    title: proteinQuestion,
  },
  proteinSupplement: {
    eyebrow: eyebrowLifestyle,
    options: supplementOptions,
    title: supplementTitle,
  },
  sex: {
    eyebrow: eyebrowYou,
    helper: sexHelper,
    options: sexOptions,
    title: sexQuestion,
  },
};

export function ProfileChoiceEditor({
  host,
  field,
}: EditorProps & { readonly field: ProfileChoiceField }) {
  const spec = CHOICES[field];
  const { read, form, flow } = useProfileEditor<string | null>(
    host,
    (profile) => (profile?.[field] as string | null | undefined) ?? null,
    (value) => ({ [field]: value }) as Partial<ProfileInput>
  );
  const stored = (read.data?.[field] as string | null | undefined) ?? null;
  return (
    <EditorShell
      disabled={form.draft === null || form.draft === stored}
      eyebrow={spec.eyebrow()}
      failure={flow.failure ?? strayError(form.errors, [field])}
      host={host}
      loadFailed={read.loadFailed}
      loading={read.loading}
      onSave={form.submit}
      pending={flow.pending}
      title={spec.title()}
    >
      <ChoiceForm
        error={form.errors[field] === undefined ? undefined : errorField()}
        helper={spec.helper?.()}
        label={spec.title()}
        onChange={form.setDraft}
        options={spec.options()}
        value={form.draft}
      />
    </EditorShell>
  );
}
